/**
 * The multipart upload the Data Catalog has always used: one
 * `POST /v2/files/upload` per file, with real byte progress off `xhr.upload`.
 *
 * Lifted out of `AddDataModal.tsx` unchanged so the workflow file picker can
 * use the SAME uploader rather than a second one. It deliberately stays an
 * `XMLHttpRequest` — `fetch()` still has no upload-progress event, and the
 * progress bar is the point.
 */

import { config } from '@/config'
import { ensureFreshToken } from '@/config/keycloakClient'
import { generateCorrelationId } from '@/utils/correlationId'
import { CORRELATION_ID_HEADER } from '@/constants/headers'
import { parseUploadResponse, type UploadResponse } from './uploadResponse'

export interface UploadOptions {
  properties?: Record<string, unknown>
  /** When true, server appends "(1)" etc. on filename collisions. Defaults
   *  to true to preserve the previous behavior when callers don't supply
   *  an explicit choice; the duplicate-file modal flips it to false when
   *  the user picks "Replace". */
  autoRename?: boolean
  /** When set, server deletes the existing doc with this id and uses the
   *  new upload's filename verbatim. Used by the "Replace" path. */
  replaceDocumentId?: string
  /** ISO timestamp the server stamps as upload_started_at. Passing it lets the
   *  caller reuse the exact value for the optimistic row (FS-555) so it sorts
   *  and reconciles consistently with the server row. Defaults to now. */
  uploadStartedAt?: string
  /** FS-499: id of a row already created by `POST /v2/files/register-pending`.
   *  Sending it makes this upload CLAIM that row rather than insert a new one,
   *  so the catalog entry the user is watching is the one that fills in. Without
   *  it a pre-registered row would be orphaned and a duplicate created beside
   *  it. `apiService.uploadFile` has always sent this; the XHR uploader could
   *  not, which is why the V2 modal had to defer its catalog row to completion. */
  docId?: string
}

export async function uploadFileWithProgress(
  file: File,
  fileType: string,
  onProgress: (percent: number) => void,
  signal?: AbortSignal,
  options?: UploadOptions,
): Promise<UploadResponse | undefined> {
  // Resolve the auth token before constructing the Promise so the executor
  // itself stays synchronous (async executors swallow errors as unhandled
  // rejections — see no-async-promise-executor).
  const token = await ensureFreshToken()
  const correlationId = generateCorrelationId()

  return new Promise<UploadResponse | undefined>((resolve, reject) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('filename', file.name)
    formData.append('fileType', fileType)
    formData.append('upload_started_at', options?.uploadStartedAt ?? new Date().toISOString())
    if (options?.autoRename !== false) {
      formData.append('auto_rename', 'true')
    }
    if (options?.replaceDocumentId) {
      formData.append('replace_document_id', options.replaceDocumentId)
    }

    if (options?.properties) {
      formData.append('properties', JSON.stringify(options.properties))
    }

    // FS-499: claim the pre-registered pending row instead of creating a new
    // document. Same field name the `fetch`-based uploader uses, read by
    // `files.controller.ts` @Body('doc_id').
    if (options?.docId) {
      formData.append('doc_id', options.docId)
    }

    const xhr = new XMLHttpRequest()
    const baseUrl = config.apiBaseUrl.replace(/\/+$/, '')
    xhr.open('POST', `${baseUrl}/v2/files/upload`)

    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.setRequestHeader(CORRELATION_ID_HEADER, correlationId)

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        // Cap at 90% — remaining 10% is server-side processing
        onProgress(Math.min(90, Math.round((e.loaded / e.total) * 90)))
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // FS-555: surface the server document id so callers can optimistically
        // insert the uploaded row before the post-upload refetch lands.
        resolve(parseUploadResponse(xhr.responseText))
      } else {
        let msg = `Upload failed (${xhr.status})`
        try {
          const data = JSON.parse(xhr.responseText)
          msg = data.detail || data.error || data.message || msg
        } catch { /* use default */ }
        reject(new Error(msg))
      }
    }

    xhr.onerror = () => reject(new Error('Network error during upload'))
    xhr.onabort = () => reject(new Error('Upload cancelled'))

    // Support abort via signal.
    //
    // The `aborted` check is not redundant with the listener. `ensureFreshToken()`
    // is awaited ABOVE, so a caller that cancels during that wait — or that hands
    // in an already-aborted signal — has already dispatched the event by the time
    // this runs, and `addEventListener` would never see it. Without the check,
    // `send()` starts an upload nobody wants and a file lands in the catalog
    // after the cancel.
    if (signal?.aborted) {
      reject(new Error('Upload cancelled'))
      return
    }
    if (signal) {
      signal.addEventListener('abort', () => xhr.abort(), { once: true })
    }

    xhr.send(formData)
  })
}
